import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import HouseholdHeader from "../components/HouseholdHeader";
import HouseholdContent from "../components/HouseholdContent";
import UploadDataModal from "../components/UploadDataModal";
import axiosInstance from "../api/axiosInstance";
import * as docusignAPI from "../api/docusignAPI";
import { useToast } from "../context/ToastContext";
import { humanizeError } from "../utils/userFriendlyError";

function HouseholdDetail({ user }) {
  const { toast } = useToast();
  const { projectId, householdId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Initialize from navigation state if available to avoid refetching
  const [household, setHousehold] = useState(location.state?.household || null);
  const [project, setProject] = useState(location.state?.project || null);
  const [loading, setLoading] = useState(!location.state?.household);
  const [missingFields, setMissingFields] = useState(
    location.state?.household?.missing_fields || null,
  );
  const [showUploadDataModal, setShowUploadDataModal] = useState(false);
  const [downloadingForms, setDownloadingForms] = useState(false);
  const [sendingToClient, setSendingToClient] = useState(false);
  const [sendingForSignature, setSendingForSignature] = useState(false);
  const [sendForSignatureEnabled, setSendForSignatureEnabled] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        if (!project) {
          const projectResponse = await axiosInstance.get(`/projects/${projectId}`);
          setProject(projectResponse.data);
        }

        if (!household) {
          const householdResponse = await axiosInstance.get(`/households/${householdId}`);
          setHousehold(householdResponse.data);
          
          if (householdResponse.data.missing_fields) {
            setMissingFields(householdResponse.data.missing_fields);
          } else {
            setMissingFields(null);
          }
        }
      } catch (error) {
        toast.error(humanizeError(error));
      } finally {
        setLoading(false);
      }
    };

    if (!household || !project) {
      fetchData();
    }
  }, [projectId, householdId]);

  useEffect(() => {
    const fetchIntegrations = async () => {
      try {
        const data = await docusignAPI.getIntegrations();
        setSendForSignatureEnabled(data.docusign === true);
      } catch {
        setSendForSignatureEnabled(false);
      }
    };
    if (projectId && householdId) {
      fetchIntegrations();
    }
  }, [projectId, householdId]);

  const fetchProjectAndHousehold = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      // Fetch project details
      const projectResponse = await axiosInstance.get(`/projects/${projectId}`);
      setProject(projectResponse.data);

      // Fetch household details
      const householdResponse = await axiosInstance.get(`/households/${householdId}`);
      setHousehold(householdResponse.data);
      
      if (householdResponse.data.missing_fields) {
        setMissingFields(householdResponse.data.missing_fields);
      } else {
        setMissingFields(null);
      }
    } catch (error) {
      toast.error(humanizeError(error));
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const silentRefreshHousehold = useCallback(async () => {
    try {
      const householdResponse = await axiosInstance.get(`/households/${householdId}`);
      setHousehold(householdResponse.data);
      if (householdResponse.data.missing_fields) {
        setMissingFields(householdResponse.data.missing_fields);
      } else {
        setMissingFields(null);
      }
    } catch (error) {
      console.error("Failed to refresh household data:", error);
    }
  }, [householdId]);

  const handleHouseholdUpdate = useCallback(
    (updatedHousehold) => {
      if (updatedHousehold === null) {
        silentRefreshHousehold();
      } else if (updatedHousehold) {
        setHousehold(updatedHousehold);
        if (updatedHousehold.missing_fields) {
          setMissingFields(updatedHousehold.missing_fields);
        } else {
          setMissingFields(null);
        }
      }
    },
    [silentRefreshHousehold],
  );

  const handleDownloadForms = async () => {
    setDownloadingForms(true);
    try {
      const response = await axiosInstance.get(
        `/households/project/${projectId}/households/${householdId}/download-all`,
        {
          responseType: 'blob',
          headers: {
            'Accept': 'application/zip'
          }
        }
      );
      
      let filename = 'All_Forms.zip';
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        let filenameMatch = contentDisposition.match(/filename="([^"]+)"/i);
        if (!filenameMatch) {
          filenameMatch = contentDisposition.match(/filename=([^;]+)/i);
        }
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].trim();
        }
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(humanizeError(error));
    } finally {
      setDownloadingForms(false);
    }
  };

  const handleSendToClient = async (advisorNote = null) => {
    setSendingToClient(true);
    try {
      await axiosInstance.post(`/households/${householdId}/send-to-client`, {
        advisor_note: advisorNote || null
      });
      toast.success("Sent to client.");
      await fetchProjectAndHousehold(true);
    } catch (error) {
      toast.error(humanizeError(error));
    } finally {
      setSendingToClient(false);
    }
  };

  const handleBack = () => {
    navigate(`/project/${projectId}`);
  };

  const handleReviewDocuments = () => {
    // Pass current data to avoid re-fetching on the documents page
    navigate(`/project/${projectId}/house/${householdId}/documents`, {
      state: { project, household }
    });
  };

  const handleSendForSignature = async (accountForms = null, firmDocs = null) => {
    setSendingForSignature(true);
    try {
      const data = await docusignAPI.prepareForSignature(projectId, householdId, accountForms, firmDocs);
      toast.success("A draft DocuSign envelope has been created for review and sending.");
      if (data.household_status) {
        setHousehold(prev => ({
          ...prev,
          status: data.household_status,
          sub_status: data.household_sub_status,
          updated_at: new Date().toISOString()
        }));
      }
      return true;
    } catch (error) {
      toast.error(humanizeError(error));
      return false;
    } finally {
      setSendingForSignature(false);
    }
  };

  const hasPrimaryMemberWithEmail = () => {
    return household?.members?.some(member => 
      member.is_primary && member.personal_info?.email
    );
  };

  const handleMarkPendingForExecution = async () => {
    try {
      const response = await axiosInstance.put(`/households/${householdId}/mark-pending-for-execution`);
      setHousehold(prev => ({
        ...prev,
        status: response.data.status,
        sub_status: response.data.sub_status,
        updated_at: new Date().toISOString()
      }));
    } catch (error) {
      throw error;
    }
  };
  
  const handleMarkDocusignInitiated = async () => {
    try {
      const response = await axiosInstance.put(`/households/${householdId}/mark-docusign-initiated`);
      setHousehold(prev => ({
        ...prev,
        status: response.data.status,
        sub_status: response.data.sub_status,
        updated_at: new Date().toISOString()
      }));
    } catch (error) {
      throw error;
    }
  };

  const handleMarkCompleted = async () => {
    try {
      const response = await axiosInstance.put(`/households/${householdId}/mark-completed`);
      setHousehold(prev => ({
        ...prev,
        status: response.data.status,
        sub_status: response.data.sub_status,
        updated_at: new Date().toISOString()
      }));
    } catch (error) {
      throw error;
    }
  };

  const handleHouseholdStatusPatch = useCallback((patch) => {
    if (!patch) return;
    setHousehold((prev) => ({
      ...prev,
      status: patch.status,
      sub_status: patch.sub_status,
      updated_at: new Date().toISOString(),
    }));
  }, []);

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar user={user} />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header user={user} />
          <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-50">
            <div className="container mx-auto px-6 py-8">
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto"></div>
                  <p className="mt-4 text-gray-600 font-medium">Loading household details</p>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (!household || !project) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar user={user} />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header user={user} />
          <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-50">
            <div className="container mx-auto px-6 py-8">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-gray-900">Household not found</h2>
                <p className="mt-2 text-gray-600">The requested household could not be found.</p>
                <button
                  onClick={handleBack}
                  className="mt-4 btn-primary"
                >
                  Back to Project
                </button>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar user={user} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header user={user} />
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-50">
          <div className="container mx-auto px-6 py-8">
            {/* Back Button */}
            <div className="mb-6">
              <button
                onClick={handleBack}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Project Dashboard
              </button>
            </div>

            {/* Household Header - 30% */}
            <div className="mb-8">
              <HouseholdHeader
                household={household}
                project={project}
                user={user}
                onDownloadForms={handleDownloadForms}
                onSendToClient={handleSendToClient}
                onEnrichData={() => setShowUploadDataModal(true)}
                onReviewDocuments={handleReviewDocuments}
                onSendForSignature={handleSendForSignature}
                sendForSignatureEnabled={sendForSignatureEnabled}
                downloadingForms={downloadingForms}
                sendingToClient={sendingToClient}
                sendingForSignature={sendingForSignature}
                hasPrimaryMemberWithEmail={hasPrimaryMemberWithEmail()}
                onMarkPendingForExecution={handleMarkPendingForExecution}
                onMarkDocusignInitiated={handleMarkDocusignInitiated}
                onMarkCompleted={handleMarkCompleted}
                onHouseholdStatusPatch={handleHouseholdStatusPatch}
                onHouseholdRefresh={silentRefreshHousehold}
              />
            </div>

            {/* Household Content - 70% */}
            <div className="flex-1">
              <HouseholdContent
                household={household}
                missingFields={missingFields}
                aumAvailability={project?.aum_availability}
                custodianTypes={project?.custodian_types}
                custodian={project?.custodian}
                destinationFirm={project?.destination_firm}
                onHouseholdUpdate={handleHouseholdUpdate}
                user={user}
              />
            </div>
          </div>
        </main>
      </div>

      {/* Upload Data Modal */}
      {showUploadDataModal && (
        <UploadDataModal
          isOpen={showUploadDataModal}
          onClose={() => setShowUploadDataModal(false)}
          selectedHousehold={household}
          projectId={projectId}
          onUploadSuccess={() => {
            setShowUploadDataModal(false);
          }}
        />
      )}
    </div>
  );
}

export default HouseholdDetail;